import React from 'react';
import { Popover, PopoverActionsBar } from '@progress/kendo-react-tooltip';
import { Button } from '@progress/kendo-react-buttons';
import { ListView, ListViewItemWrapper } from '@progress/kendo-react-listview';

/**
 * Info Icon With Popover component.
 * @component
 * @returns {React.ReactElement}
 */
const InfoIconWithPopover = ({ dataItem }) => {
  const [showPopover, setShowPopover] = React.useState(false);
  const infoIconRef = React.useRef(null);

  const textValue = dataItem?.UcOssiStepArgumentsDict?.TEXT ?? '';
  const varnamValue = dataItem?.UcOssiStepArgumentsDict?.VARNAM ?? '';

  // Data for the ListView
  const listData = [
    { id: 1, label: 'TEXT', value: textValue },
    { id: 2, label: 'VARNAM', value: varnamValue },
  ];

  const itemRender = (props) => {
    const item = props?.dataItem;
    return (
      <>
        {item?.value && (
          <ListViewItemWrapper>
            <div className='item-main'>
              <strong className='item-label'>{item?.label} :</strong>{' '}
              <span>{item?.value}</span>
            </div>
          </ListViewItemWrapper>
        )}
      </>
    );
  };

  const onClick = React.useCallback(() => {
    setShowPopover(!showPopover);
  }, [setShowPopover, showPopover]);

  return (
    (textValue || varnamValue) && (
      <>
        <span
          className='indicator-icon sis-ico-circle-info-solid'
          ref={infoIconRef}
          style={{ cursor: 'pointer', marginBlock: 'auto' }}
          onClick={onClick}
        ></span>
        <Popover
          show={showPopover}
          anchor={infoIconRef.current}
          position='right'
          className={'dictionary-popup'}
        >
          <div className='popup-body'>
            <ListView data={listData} item={itemRender} />
          </div>
          <PopoverActionsBar>
            <div className='k-d-flex k-justify-content-end'>
              <Button onClick={onClick} type='button'>
                Close
              </Button>
            </div>
          </PopoverActionsBar>
        </Popover>
      </>
    )
  );
};
const DataColumns = ({
  parentFlowId,
  onAddChild,
  onEditStep,
  onEditAppFlow,
  deleteFlowStepDialog,
  fetchArgumentsForStep,
  detailsDataModified,
}) => {
  // This function will be called for each row to create the info icon with popover

  return [
    {
      title: 'Actions',
      width: '20px',
      cell: (props) => (
        <td>
          <div className='k-d-flex k-justify-content-end'>
            {props?.dataItem?.StepType === 'APPFLOW' && detailsDataModified && (
              <>
                <span
                  className='indicator-icon sis-ico-add !k-font-size-lg'
                  title='Add'
                  onClick={() => onAddChild(props.dataItem)}
                  style={{ cursor: 'pointer', color: '#4caf50' }}
                ></span>
              </>
            )}
            {detailsDataModified && (
              <>
                <span
                  className='indicator-icon sis-ico-edit-1 !k-font-size-lg'
                  title='Edit'
                  onClick={() => onEditStep(props.dataItem)}
                  style={{ cursor: 'pointer', color: '#000' }}
                ></span>
                <span
                  className='indicator-icon sis-ico-bin-2 !k-font-size-lg'
                  title='Delete'
                  onClick={() => deleteFlowStepDialog(props.dataItem)}
                  style={{ cursor: 'pointer', color: '#f44336' }}
                ></span>
              </>
            )}

            <span
              className={`indicator-icon !k-font-size-lg ${props?.dataItem?.IsDetailArguments ? 'sis-ico-filter' : 'sis-ico-no-filter'}`}
              title={
                props?.dataItem?.IsDetailArguments
                  ? 'Arguments'
                  : 'No Argument Found'
              }
              style={{
                cursor: 'pointer',
                color: props?.dataItem?.IsDetailArguments
                  ? '#3f51b5'
                  : '#f44336',
              }}
              onClick={() => fetchArgumentsForStep(props?.dataItem)}
            ></span>
          </div>
        </td>
      ),
    },
    {
      title: '',
      width: '20px',
      cell: (props) => (
        <td>
          <div className='k-d-flex k-text-center'>
            {props?.dataItem?.UcOssiStepRunTillExpr && (
              <>
                <span
                  className='indicator-icon sis-ico-while-loop'
                  title='While Loop'
                ></span>
              </>
            )}
            {props?.dataItem?.UcOssiExecCntExpr && (
              <>
                <span
                  className='indicator-icon sis-ico-for-loop'
                  title='For Loop'
                ></span>
              </>
            )}
            {props?.dataItem?.IsPreCondition && (
              <>
                <span
                  className='indicator-icon sis-ico-pre-condition'
                  title='Pre Condition Expression'
                ></span>
              </>
            )}
            <InfoIconWithPopover dataItem={props.dataItem} />
          </div>
        </td>
      ),
    },
    { field: 'UcOssiAfStepId', title: 'Step ID', expandable: true },
    {
      field: 'UcOssiAfId',
      title: 'Flow ID',
      cell: (props) => {
        const isSameAsParentFlow = parentFlowId === props?.dataItem?.UcOssiAfId;
        return (
          <td>
            <span
              onClick={
                !isSameAsParentFlow
                  ? () => onEditAppFlow(props.dataItem)
                  : undefined
              }
              className={`${!isSameAsParentFlow ? 'edit-link' : ''}`}
              title={props.dataItem.UcOssiAfId}
            >
              {props.dataItem.UcOssiAfId}
            </span>
          </td>
        );
      },
    },
    {
      field: 'StepType',
      title: 'Step Type',
      cell: (props) => {
        const getDotClass = (stepType) => {
          switch (stepType) {
            case 'APPFLOW':
              return 'approved';
            case 'DEFAFSTP':
              return 'info';
            case 'PREDEFLST':
              return 'new';
            default:
              return 'default'; // Fallback dot
          }
        };
        return (
          <td>
            <div className='k-d-flex'>
              <span
                className={`badge k-d-flex k-align-items-center`}
                title={props?.dataItem?.StepTypeDisplayName}
              >
                <span
                  className={`circle ${getDotClass(props.dataItem.StepType)}`}
                />
              </span>
              {props?.dataItem?.StepTypeDisplayName}
            </div>
          </td>
        );
      },
    },
  ];
};

export default DataColumns;
